import { useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { apiEntrega, type Entrega as DatosEntrega } from '../api';
import { useApi, mensajeDeError } from '../lib/useApi';
import { Cargando, Marca, Numero } from '../components/ui';
import { abrir, fila, resorte, suave } from '../components/movimiento';
import { aviso, botonPrimario, c, campo, etiquetaCampo, fechaCorta, filaTabla, tarjeta, HUSO } from '../theme';

/** Nombre en criollo de cada extensión que acepta el enlace, para no mostrar «.pdf .xlsx». */
const NOMBRE_DE_EXTENSION: Record<string, string> = {
  '.xlsx': 'Excel',
  '.xls': 'Excel',
  '.csv': 'CSV',
  '.pdf': 'PDF',
  '.eml': 'mail',
  '.msg': 'mail',
  '.txt': 'texto',
};

/** «Excel, CSV o PDF», a partir de las extensiones que acepta el enlace. */
function nombresDeExtensiones(extensiones: string[]): string {
  const nombres = [...new Set(extensiones.map((e) => NOMBRE_DE_EXTENSION[e] ?? e.replace('.', '').toUpperCase()))];
  if (nombres.length <= 1) return nombres[0] ?? '';
  return `${nombres.slice(0, -1).join(', ')} o ${nombres.at(-1)}`;
}

/**
 * La pantalla del enlace de entrega: subir un archivo sin tener usuario.
 *
 * Es la única parte del sistema que se ve sin iniciar sesión, y está afuera del
 * layout a propósito: quien entra por acá no es del depósito, no tiene rol y no
 * tiene por qué ver el menú ni ninguna otra pantalla. Sube, ve lo que subió, y
 * eso es todo.
 *
 * Dos decisiones que parecen detalles y no lo son:
 *
 *   - **No usa ningún icono.** Los iconos son una tipografía servida por Google
 *     y del otro lado hay un proxy corporativo que bloquea servicios de
 *     Google: si no carga, un `<Icono>` se ve como la palabra «upload_file».
 *     Acá los botones dicen lo que hacen con letras.
 *   - **No carga el acceso con Google.** Es justamente lo que hace que esta
 *     puerta funcione donde la otra no.
 */
export default function Entrega({ token }: { token: string }) {
  const datos = useApi<DatosEntrega>(() => apiEntrega.ver(token), [token]);

  const [nota, setNota] = useState('');
  const [error, setError] = useState('');
  const [listo, setListo] = useState('');
  const [ocupado, setOcupado] = useState(false);

  async function subir(f: File | undefined) {
    if (!f) return;
    setOcupado(true);
    setError('');
    setListo('');
    try {
      const r = await apiEntrega.subir(token, f, nota);
      setListo(`${r.nombre} · ${r.peso}`);
      setNota('');
      datos.recargar();
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setOcupado(false);
    }
  }

  const marco = (contenido: ReactNode) => (
    <div style={{ minHeight: '100vh', background: c.fondo, padding: '32px 16px' }}>
      <div style={{ maxWidth: 640, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Marca tamano={40} />
        {contenido}
      </div>
    </div>
  );

  if (datos.cargando) return marco(<Cargando texto="Abriendo el enlace…" />);

  // Un enlace vencido, dado de baja o mal copiado no es un error del sistema:
  // es información para quien lo abrió, con el motivo que le dio el servidor.
  if (datos.error || !datos.datos) {
    return marco(
      <motion.div {...abrir(16)} style={{ ...tarjeta, padding: 24, display: 'grid', gap: 8 }}>
        <span style={{ fontSize: 19, fontWeight: 600, color: c.texto }}>Este enlace no se puede usar</span>
        <span style={{ fontSize: 14, color: c.texto2, textWrap: 'pretty' }}>
          {datos.error ?? 'No pudimos abrir el enlace.'}
        </span>
      </motion.div>,
    );
  }

  const d = datos.datos;
  const accept = d.extensiones.join(',');
  const sinLugar = d.restantes === 0;

  return marco(
    <>
      {/* Las tarjetas se abren desde el centro, una detrás de la otra. */}
      <motion.div {...abrir(16)} style={{ ...tarjeta, padding: 24, display: 'grid', gap: 10 }}>
        <span style={{ fontSize: 23, fontWeight: 600, letterSpacing: '-0.03em', color: c.texto }}>
          Dejale un archivo a RDM Logística
        </span>
        <span style={{ fontSize: 14, color: c.texto2, textWrap: 'pretty' }}>
          Este enlace es de <strong>{d.rotulo}</strong>. El archivo queda guardado en el sistema del depósito y lo
          descarga el equipo de RDM para revisarlo. No hace falta cuenta ni contraseña.
        </span>
        <span style={{ fontSize: 13, color: c.texto3 }}>
          Se aceptan {nombresDeExtensiones(d.extensiones)}, hasta 25 MB.{' '}
          {sinLugar
            ? 'Ya no entran más archivos'
            : <>Entran <Numero valor={d.restantes} formato={String} /> {d.restantes === 1 ? 'archivo más' : 'archivos más'}</>}{' '}
          y el enlace vale hasta el{' '}
          {fechaCorta(d.vence)}.
        </span>
      </motion.div>

      <AnimatePresence>
      {error && (
        <motion.div key={`err-${error}`} {...abrir(16)} role="alert" style={{ ...aviso('err'), fontSize: 14, color: c.texto, textWrap: 'pretty' }}>
          {error}
        </motion.div>
      )}

      {listo && (
        <motion.div key={`ok-${listo}`} {...abrir(16)} role="status" style={{ ...aviso('ok'), display: 'grid', gap: 4 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: c.texto }}>Llegó, gracias</span>
          <span style={{ fontSize: 13.5, color: c.texto2 }}>{listo}</span>
          <span style={{ fontSize: 13, color: c.texto2 }}>
            Ya lo pueden ver desde el sistema. Si te falta mandar otro, podés subirlo acá mismo.
          </span>
        </motion.div>
      )}
      </AnimatePresence>

      <motion.div
        {...abrir(16)}
        // Quieta, sin recorte: el campo y el botón no pueden perder el anillo de foco.
        animate={{ ...abrir(16).animate, transitionEnd: { clipPath: 'none' } }}
        transition={{ ...abrir(16).transition, delay: 0.06 }}
        style={{ ...tarjeta, padding: 24, display: 'grid', gap: 12 }}
      >
        <label style={etiquetaCampo} htmlFor="nota-entrega">
          De qué se trata (opcional)
        </label>
        <input
          id="nota-entrega"
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          maxLength={300}
          placeholder="Maestro de artículos actualizado"
          style={campo}
        />

        <label
          style={{
            ...botonPrimario,
            position: 'relative',
            minHeight: 52,
            justifyContent: 'center',
            opacity: ocupado || sinLugar ? 0.5 : 1,
          }}
        >
          {/* «Subiendo…» reemplaza al rótulo: el viejo sale y el nuevo entra. */}
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={ocupado ? 'subiendo' : sinLugar ? 'completo' : 'elegir'}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6, transition: { duration: 0.12 } }}
              transition={suave}
            >
              {ocupado ? 'Subiendo…' : sinLugar ? 'Este enlace ya está completo' : 'Elegir archivo y subirlo'}
            </motion.span>
          </AnimatePresence>
          <input
            type="file"
            accept={accept}
            disabled={ocupado || sinLugar}
            onChange={(e) => {
              void subir(e.target.files?.[0]);
              e.target.value = '';
            }}
            style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
          />
        </label>
      </motion.div>

      {/* La lista aparece con el primer archivo; cada archivo nuevo entra
          arriba y empuja a los de abajo. */}
      <AnimatePresence>
      {d.entregados.length > 0 && (
        <motion.div key="entregados" {...abrir(16)} style={{ ...tarjeta, overflow: 'hidden', position: 'relative' }}>
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${c.borde}`, fontSize: 15, fontWeight: 600, color: c.texto }}>
            Lo que ya entregaste
          </div>
          {/* En el celular el nombre va solo arriba y el peso y la hora abajo:
              en un renglón no entraban y la tarjeta cortaba la hora. */}
          <AnimatePresence mode="popLayout">
          {d.entregados.map((a, i) => (
            <motion.div
              key={`${a.nombre}-${a.subidoEn}`}
              layout
              {...fila(i)}
              transition={{ ...fila(i).transition, layout: resorte }}
              className="flex-wrap gap-3 max-tablet:gap-y-1 tablet:flex-nowrap"
              style={{ ...filaTabla, gap: undefined }}
            >
              <span className="min-w-[200px] flex-1 max-tablet:basis-full" style={{ display: 'grid', gap: 2 }}>
                <span style={{ fontWeight: 500, wordBreak: 'break-all' }}>{a.nombre}</span>
                {a.nota && <span style={{ fontSize: 12.5, color: c.texto2 }}>{a.nota}</span>}
              </span>
              <span className="w-20 text-right max-tablet:w-auto max-tablet:text-left" style={{ fontSize: 12.5, color: c.texto2 }}>
                {a.peso}
              </span>
              <span style={{ width: 120, fontSize: 12.5, color: c.texto2, fontVariantNumeric: 'tabular-nums' }}>
                {new Date(a.subidoEn).toLocaleString('es-AR', {
                  timeZone: HUSO,
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </motion.div>
          ))}
          </AnimatePresence>
        </motion.div>
      )}
      </AnimatePresence>

      <span style={{ fontSize: 12.5, color: c.texto3, textAlign: 'center', textWrap: 'pretty' }}>
        Si el enlace dejó de funcionar, pedile uno nuevo a la persona de RDM con la que venís hablando.
      </span>
    </>,
  );
}
